import React, { useEffect } from 'react';
import { usePWA } from '../../hooks/usePWA';

interface PWAInteractionTrackerProps {
  children: React.ReactNode;
}

const PWAInteractionTracker: React.FC<PWAInteractionTrackerProps> = ({ children }) => {
  const { incrementInteraction } = usePWA();

  useEffect(() => {
    // Track significant user interactions
    const handleInteraction = () => {
      incrementInteraction();
    };

    // Events that count as meaningful interactions
    const events = [
      'click',  // Any click
      'touchstart', // Touch events on mobile
    ];

    // Track interactions on specific elements
    const trackableSelectors = [
      'button',           // All buttons
      'a',               // All links
      '[role="button"]',  // Elements acting as buttons
      '.nav-link',       // Navigation links
      '.card',           // Card components
      '.menu-item',      // Menu items
    ];

    const handleElementInteraction = (event: Event) => {
      const target = event.target as Element;
      
      // Check if the clicked element matches our trackable selectors
      const isTrackable = trackableSelectors.some(selector => {
        return target.matches?.(selector) || target.closest?.(selector);
      });

      if (isTrackable) {
        // Throttle interactions to avoid counting rapid clicks
        const lastInteraction = parseInt(localStorage.getItem('pwa_last_interaction') || '0');
        const now = Date.now();
        
        // Only count if more than 5 seconds have passed since last interaction
        if (now - lastInteraction > 5000) {
          localStorage.setItem('pwa_last_interaction', now.toString());
          handleInteraction();
        }
      }
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleElementInteraction, { passive: true });
    });

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleElementInteraction);
      });
    };
  }, [incrementInteraction]);

  return <>{children}</>;
};

export default PWAInteractionTracker;